/**
 * THE COMPONENT SET — copied verbatim from the consumer app's
 * src/workspace/ui.jsx, and kept identical to it.
 *
 *   "the look and feel of the admin portal should EXACTLY match that of the
 *    consumer profile screens. One visual language."
 *
 * Not a lookalike. The same file, so the same Card radius, the same hairline,
 * the same Deep Trust Green, the same DM Sans. A portal styled to RESEMBLE the
 * product drifts from it the first time either side is touched; a portal that
 * imports the same primitives cannot.
 *
 * The Visual Language Canon §1 locks this light-first, white-card, deep-green-
 * and-gold system for "every operational screen", and the dark cinematic
 * treatment is "reserved exclusively for the public marketing website's
 * storytelling sections ... never for operational screens". The admin portal is
 * an operational screen, so the founder's ruling and canon agree.
 *
 * ONE THING I ARGUED FOR AND LOST, RECORDED SO IT IS A DECISION RATHER THAN AN
 * OVERSIGHT: the first version of this portal was deliberately dark and
 * purple-accented, so that a staff tab and a member tab could never be confused
 * at a glance — the concern being a reasoned, logged action taken on the wrong
 * screen. The founder ruled for one visual language. The distinction now has to
 * be carried by words rather than by colour, which is why every portal screen
 * names itself "Orchid Portal" in its header and why the sign-in says "Internal
 * system. Your access is recorded."
 *
 * If this file and the consumer one ever diverge, the consumer one is right.
 */

import { useMemo, useRef } from 'react';

// ---------------------------------------------------------------------------
// COLOUR -- Visual Language Canon §2, verbatim
// ---------------------------------------------------------------------------
export const O = {
  green:      '#0D3D2A',  // Deep Trust Green, primary
  greenLight: '#1A5C3E',
  gold:       '#C9A84C',  // earned status only -- never a generic "good"
  goldDeep:   '#A8782A',
  canvas:     '#F7F9F7',  // page background
  card:       '#FFFFFF',  // card fill
  ink:        '#0D2418',  // primary text
  inkMuted:   'rgba(13,36,24,0.58)',
  inkFaint:   'rgba(13,36,24,0.40)',
  hairline:   'rgba(13,61,42,0.10)',
  hairlineOn: 'rgba(13,61,42,0.18)',
  critical:   '#A8352A',
  warning:    '#D68910',
  info:       '#2F6FB0',
};

export const SANS = "'DM Sans','Helvetica Neue',Arial,sans-serif";
// Georgia is reserved for the wordmark, display headline numerals, and
// certificates. It never carries UI body text or data (§3, §11).
export const DISPLAY = 'Georgia,serif';

// ---------------------------------------------------------------------------
// CARD & CHIP LANGUAGE -- §7
// ---------------------------------------------------------------------------

export const Card = ({ children, style = {}, onClick, as = 'div' }) => {
  const Tag = as;
  return (
    <Tag
      onClick={onClick}
      style={{
        background: O.card,
        border: `1px solid ${O.hairline}`,
        borderRadius: 18,
        boxShadow: '0 1px 2px rgba(13,36,24,0.04), 0 8px 24px rgba(13,36,24,0.05)',
        padding: 20,
        boxSizing: 'border-box',
        textAlign: 'left',
        width: '100%',
        // A CARD MUST BE ABLE TO SHRINK. A grid or flex item defaults to
        // `min-width: auto`, which means it refuses to be narrower than its
        // widest content -- so a card holding the field onboarding ledger, a
        // seven-column table with a 520px floor, pushed the whole Portal
        // sideways on a phone: 604px of document in a 390px viewport, and every
        // card below it clipped at the right edge.
        //
        // The table already scrolls inside its own overflow container. That
        // container could never take effect while its parent was allowed to
        // grow to meet it, which is what this one property stops.
        minWidth: 0,
        cursor: onClick ? 'pointer' : undefined,
        font: 'inherit',
        color: 'inherit',
        ...style,
      }}
    >
      {children}
    </Tag>
  );
};

/** A leading icon inside a small tinted circular chip. §7. */
export const IconChip = ({ children, tint = O.green, size = 34 }) => (
  <div style={{
    width: size, height: size, borderRadius: '50%', flexShrink: 0,
    background: `${tint}14`, border: `1px solid ${tint}2E`,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: tint,
  }}>{children}</div>
);

/** Solid fill, fully rounded, one or two words. §7. */
export const StatusPill = ({ children, tone = 'green' }) => {
  const tones = {
    green:    { bg: O.green,    fg: '#FFFFFF' },
    gold:     { bg: O.gold,     fg: '#2A1F05' },
    info:     { bg: O.info,     fg: '#FFFFFF' },
    warning:  { bg: O.warning,  fg: '#2A1B02' },
    critical: { bg: O.critical, fg: '#FFFFFF' },
    quiet:    { bg: 'rgba(13,61,42,0.07)', fg: O.inkMuted },
  };
  const t = tones[tone] || tones.green;
  return (
    <span style={{
      display: 'inline-block', padding: '4px 10px', borderRadius: 999,
      background: t.bg, color: t.fg, fontFamily: SANS,
      fontSize: 10.5, fontWeight: 700, letterSpacing: '0.04em', whiteSpace: 'nowrap',
    }}>{children}</span>
  );
};

// ---------------------------------------------------------------------------
// THE SCORE RING -- §6, binding on every surface
//
// "A trust score is always shown as: a large numeral over its ceiling
// ('94 / 100'); inside or beside a circular progress ring in a green-to-gold
// gradient proportional to the score; with its band label immediately below in
// small caps." No module may invent an alternative visualisation.
//
// One component, used for every score on every screen, is the only way that
// stays true. Sub-metrics use the same component at a smaller size (§6), never
// a bar chart and never a bare number.
// ---------------------------------------------------------------------------

export const ScoreRing = ({ value, ceiling = 100, band, label, size = 96, showCeiling = true }) => {
  const gradientId = useMemo(() => `ring-${Math.random().toString(36).slice(2, 9)}`, []);
  const known = value !== null && value !== undefined && Number.isFinite(Number(value));
  const pct = known ? Math.max(0, Math.min(1, Number(value) / ceiling)) : 0;
  const stroke = size >= 72 ? 7 : 5;
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  const numeralSize = size >= 72 ? size * 0.30 : size * 0.30;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
      <div style={{ position: 'relative', width: size, height: size }}>
        <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
          <defs>
            <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={O.greenLight} />
              <stop offset="100%" stopColor={O.gold} />
            </linearGradient>
          </defs>
          <circle cx={size / 2} cy={size / 2} r={r} fill="none"
                  stroke="rgba(13,61,42,0.09)" strokeWidth={stroke} />
          {known && (
            <circle cx={size / 2} cy={size / 2} r={r} fill="none"
                    stroke={`url(#${gradientId})`} strokeWidth={stroke} strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={circumference * (1 - pct)} />
          )}
        </svg>
        <div style={{
          position: 'absolute', inset: 0, display: 'flex',
          flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        }}>
          {/* Display headline numeral -- one of the three places Georgia is
              permitted (§3). */}
          <span style={{
            fontFamily: DISPLAY, fontSize: numeralSize, fontWeight: 700,
            color: O.ink, lineHeight: 1,
          }}>{known ? Math.round(Number(value)) : '–'}</span>
          {showCeiling && (
            <span style={{ fontFamily: SANS, fontSize: size * 0.11, color: O.inkFaint, marginTop: 2 }}>
              / {ceiling}
            </span>
          )}
        </div>
      </div>
      {(band || label) && (
        <span style={{
          fontFamily: SANS, fontSize: size >= 72 ? 10 : 9, fontWeight: 700,
          letterSpacing: '0.09em', textTransform: 'uppercase',
          color: band ? O.green : O.inkMuted, textAlign: 'center', maxWidth: size + 24,
        }}>{band || label}</span>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// ONBOARDING GRAMMAR -- §9
//
// "A fixed stepper of four steps or fewer, pinned at the top -- completed steps
// shown as a filled green check-circle, the current step solid-filled, future
// steps outlined in grey."
// ---------------------------------------------------------------------------

export const Stepper = ({ steps, current }) => (
  <div style={{
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: 0, padding: '18px 0 22px',
  }}>
    {steps.map((label, i) => {
      const n = i + 1;
      const done = n < current;
      const active = n === current;
      return (
        <div key={label} style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, width: 82 }}>
            <div
              aria-current={active ? 'step' : undefined}
              data-step-state={done ? 'complete' : active ? 'current' : 'future'}
              style={{
                width: 26, height: 26, borderRadius: '50%',
                background: done || active ? O.green : 'transparent',
                border: done || active ? `2px solid ${O.green}` : '2px solid rgba(13,36,24,0.20)',
                color: done || active ? '#FFFFFF' : O.inkFaint,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: SANS, fontSize: 12, fontWeight: 800,
              }}
            >
              {done ? '✓' : n}
            </div>
            <span style={{
              fontFamily: SANS, fontSize: 10, fontWeight: active ? 700 : 500,
              color: active ? O.ink : O.inkFaint, textAlign: 'center', lineHeight: 1.3,
            }}>{label}</span>
          </div>
          {i < steps.length - 1 && (
            <div style={{
              width: 22, height: 2, marginBottom: 20,
              background: n < current ? O.green : 'rgba(13,36,24,0.14)',
            }}/>
          )}
        </div>
      );
    })}
  </div>
);

/** The single full-width, solid dark-green primary CTA. §9. One per screen. */
export const PrimaryCTA = ({ children, onClick, disabled, busy, type = 'button', style = {} }) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled || busy}
    style={{
      width: '100%', padding: '15px 20px', borderRadius: 12,
      background: disabled || busy ? 'rgba(13,61,42,0.28)' : O.green,
      color: '#FFFFFF', border: 'none',
      fontFamily: SANS, fontSize: 14, fontWeight: 700, letterSpacing: '0.01em',
      cursor: disabled || busy ? 'not-allowed' : 'pointer',
      boxShadow: disabled || busy ? 'none' : '0 6px 20px rgba(13,61,42,0.22)',
      transition: 'background 0.15s, box-shadow 0.15s',
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
      ...style,
    }}
  >
    {busy && <Spinner size={15} colour="#FFFFFF" />}
    {children}
  </button>
);

/**
 * IT HONOURS `disabled`, AND UNTIL SCRIPT 276's SCREEN IT DID NOT.
 *
 * The prop was not in the signature at all, so `<QuietButton disabled={...}>`
 * dropped it silently: a control that reads as disabled in the source and is
 * live to the person using it. Nothing in the Portal was passing it — measured
 * before changing this — so it was a trap rather than a live defect, and the
 * first caller to set foot in it was the ownership queue's refusal button,
 * where a live control would have sent a refusal with no usable reason.
 *
 * The disabled text is inkMuted, not inkFaint: 0.58 rather than 0.40 against
 * the card, because the accessibility gate records a contrast ceiling per
 * workspace and unreadable grey is a regression, not a style.
 */
export const QuietButton = ({ children, onClick, disabled = false, style = {} }) => (
  <button onClick={onClick} disabled={disabled} style={{
    width: '100%', padding: '13px 18px', borderRadius: 12,
    background: 'transparent', color: disabled ? O.inkMuted : O.green,
    border: `1px solid ${O.hairlineOn}`,
    fontFamily: SANS, fontSize: 13, fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
    ...style,
  }}>{children}</button>
);

/** The fixed footer trust line, on every screen in the onboarding flow. §9. */
export const TrustFooter = ({ style = {} }) => (
  <p style={{
    textAlign: 'center', fontFamily: SANS, fontSize: 11, fontWeight: 600,
    letterSpacing: '0.14em', textTransform: 'uppercase',
    color: O.inkFaint, margin: '26px 0 0', ...style,
  }}>Secure. Private. Trusted.</p>
);

// ---------------------------------------------------------------------------
// FORM PRIMITIVES
// ---------------------------------------------------------------------------

export const Field = ({ label, hint, error, children, htmlFor }) => (
  <div style={{ marginBottom: 16 }}>
    <label htmlFor={htmlFor} style={{
      display: 'block', fontFamily: SANS, fontSize: 12, fontWeight: 700,
      color: O.ink, marginBottom: 6,
    }}>{label}</label>
    {children}
    {error
      ? <p role="alert" style={{ fontFamily: SANS, fontSize: 11.5, color: O.critical, margin: '6px 0 0' }}>{error}</p>
      : hint ? <p style={{ fontFamily: SANS, fontSize: 11.5, color: O.inkFaint, margin: '6px 0 0', lineHeight: 1.5 }}>{hint}</p>
      : null}
  </div>
);

export const textInputStyle = (invalid) => ({
  width: '100%', boxSizing: 'border-box',
  padding: '13px 14px', borderRadius: 11,
  border: `1px solid ${invalid ? O.critical : O.hairlineOn}`,
  background: O.card, color: O.ink,
  fontFamily: SANS, fontSize: 14, outline: 'none',
});

export const Spinner = ({ size = 18, colour = O.green }) => (
  <span
    aria-hidden="true"
    style={{
      display: 'inline-block', width: size, height: size, borderRadius: '50%',
      border: `2px solid ${colour}33`, borderTopColor: colour,
      animation: 'orchidSpin 0.8s linear infinite', flexShrink: 0,
    }}
  />
);

/**
 * THE ONE LOADING MARK.
 *
 *   "The page loading state should be the green-background Orchid logo (the
 *    same one used on the marketing website home screen), for ALL profiles and
 *    all redirects from the marketing site to any profile a user is trying to
 *    log in to. That should be the ONLY image used in any loading state ...
 *    cropped to be circular ... and a gold circle spinning around it."
 *
 * One component, so "the only image" is a fact about the code rather than a
 * convention somebody has to keep. Every waiting state in both apps renders
 * this; nothing else is permitted to draw its own.
 *
 * The keyframes travel with it rather than coming from the workspace shell,
 * because the two places a person waits longest -- the sign-in screen and the
 * redirect in from the marketing site -- are both outside the shell, and a
 * spinner that does not spin reads as a frozen page.
 *
 * prefers-reduced-motion is honoured. Vestibular disorders are real, the ring
 * is decoration, and a still ring communicates the same thing.
 */
export const OrchidMark = ({ size = 88 }) => {
  const ring = Math.round(size * 1.26);
  return (
    <span
      aria-hidden="true"
      style={{
        position: 'relative', display: 'inline-flex', width: ring, height: ring,
        alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}
    >
      <style>{`
        @keyframes orchidSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .orchid-ring { animation: orchidSpin 1.15s linear infinite; }
        @media (prefers-reduced-motion: reduce) { .orchid-ring { animation: none; } }
      `}</style>
      <span
        className="orchid-ring"
        style={{
          position: 'absolute', inset: 0, borderRadius: '50%',
          border: `${Math.max(2, Math.round(size / 28))}px solid ${O.gold}22`,
          borderTopColor: O.gold,
        }}
      />
      <img
        src="/images/orchid-logo-green.png"
        alt=""
        width={size}
        height={size}
        style={{ borderRadius: '50%', objectFit: 'cover', display: 'block' }}
        // A broken image must not leave a torn box inside the ring. The ring
        // alone still reads as "loading".
        onError={(e) => { e.target.style.visibility = 'hidden'; }}
      />
    </span>
  );
};

/**
 * Loading states are screens, not spinners floating in a void. A person waiting
 * should be told what is being waited for.
 */
export const LoadingPanel = ({ message = 'Loading…' }) => (
  // data-testid, not the message text, is what a test should wait on. A helper
  // written as "wait until the word Loading is gone" passes VACUOUSLY the day
  // somebody changes the wording -- which is exactly how a suite goes green
  // while asserting nothing.
  <div role="status" data-testid="orchid-loading" style={{
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    gap: 18, padding: '56px 24px', minHeight: 220,
  }}>
    <OrchidMark size={72} />
    <p style={{ fontFamily: SANS, fontSize: 13, color: O.inkMuted, margin: 0 }}>{message}</p>
  </div>
);

/**
 * The full-page wait. Used for a cold start and for every redirect in from the
 * marketing site, where canon asks for the mark and nothing else -- no heading,
 * no card, no second image.
 */
export const FullPageLoader = ({ message = null }) => (
  <div role="status" aria-live="polite" data-testid="orchid-loading" style={{
    position: 'fixed', inset: 0, zIndex: 60, background: O.canvas,
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    gap: 20, padding: 24,
  }}>
    <OrchidMark size={96} />
    {message && (
      <p style={{ fontFamily: SANS, fontSize: 13, color: O.inkMuted, margin: 0 }}>{message}</p>
    )}
  </div>
);

export const Notice = ({ tone = 'info', children }) => {
  const tones = {
    info:     { bg: 'rgba(47,111,176,0.08)',  bd: 'rgba(47,111,176,0.28)',  fg: '#1D4B7A' },
    warning:  { bg: 'rgba(214,137,16,0.09)',  bd: 'rgba(214,137,16,0.30)',  fg: '#7A4E06' },
    critical: { bg: 'rgba(168,53,42,0.08)',   bd: 'rgba(168,53,42,0.28)',   fg: '#7A251D' },
  };
  const t = tones[tone] || tones.info;
  return (
    <div role={tone === 'critical' ? 'alert' : 'status'} style={{
      background: t.bg, border: `1px solid ${t.bd}`, borderRadius: 12,
      padding: '12px 14px', fontFamily: SANS, fontSize: 12.5, lineHeight: 1.6,
      color: t.fg, margin: '0 0 16px',
    }}>{children}</div>
  );
};

/** Keyframes the spinner needs, injected once by the workspace shell. */
export const WorkspaceKeyframes = () => (
  <style>{`
    @keyframes orchidSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
    @keyframes orchidFadeUp { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
  `}</style>
);

/**
 * SIX BOXES, AND THE SIXTH DIGIT IS THE ENTER KEY.
 *
 * The founder, 14 September 2026:
 *
 *   "when I log in, it would be great to have just six blocks in which to type
 *    the authentication number, and after typing the 6th digit, that must
 *    basically mean 'enter' and let me into the Portal is the digits are
 *    correct. No more 'Continue'."
 *
 * A code that is exactly six digits long carries its own submit signal: there
 * is nothing a person can do after the sixth character except press a button
 * saying what they have already finished saying. The button is gone.
 *
 * ---------------------------------------------------------------------------
 * FOUR THINGS THAT MAKE THIS DIFFERENT FROM SIX TEXT BOXES IN A ROW
 *
 * PASTE FILLS ALL SIX. An authenticator app is a thing people copy from, and a
 * six-box control that takes the first character of a pasted code and drops the
 * rest is worse than the single field it replaced. Pasting anywhere in the row
 * distributes the digits from that box onward.
 *
 * BACKSPACE ON AN EMPTY BOX STEPS BACK and clears the one before it, which is
 * what the key already appears to do; without it a mistyped third digit strands
 * the cursor and the person has to click.
 *
 * IT FIRES ONCE PER CODE. `onComplete` runs on the transition into a full six
 * digits, not on every render where six are present -- otherwise a failed code
 * left on screen re-submits itself on any re-render, and a locked account is
 * what that looks like from the outside.
 *
 * AND IT IS SIX INPUTS, NOT SIX DIVS WITH A KEY HANDLER. Each box is a real
 * <input> with its own label, so a screen reader says "digit 3 of 6" and a
 * password manager can still fill the row; `autoComplete="one-time-code"` sits
 * on the first, which is where the platform looks for it.
 */
export const CodeBoxes = ({
  value, onChange, onComplete, length = 6, disabled = false, invalid = false, autoFocus = false,
}) => {
  const boxes = useRef([]);
  const digits = String(value || '').slice(0, length).split('');

  const focusBox = (i) => {
    const el = boxes.current[Math.max(0, Math.min(length - 1, i))];
    if (el) { el.focus(); el.select?.(); }
  };

  /**
   * ONE PLACE THAT WRITES THE ROW, AND THE PLACE THAT SUBMITS IT.
   *
   * THE FIRST VERSION SUBMITTED FROM AN EFFECT keyed on the value, guarded by a
   * ref so it fired on the edge into a full row rather than on the level. That
   * guard COULD NOT BE MADE TO FAIL: removing it reddened nothing, because
   * `onComplete` is wrapped in useCallback and `value` only ever transitions to
   * full once, so the effect had no second chance to run. A guard whose removal
   * changes no test is a guard nobody has tested -- script 193's grandfathering
   * check, in a React file.
   *
   * Submitting from the write itself is simpler and has a failure mode that is
   * real: the row is ALREADY full and another digit arrives. `next` is still
   * six long, so a naive check fires a second time on a code that has already
   * been sent. `wasFull` is what stops that, and there is a test that reds when
   * it goes.
   */
  const write = (next, caret) => {
    const wasFull = digits.join('').length === length;
    const trimmed = next.slice(0, length);
    onChange(trimmed);
    if (caret !== undefined) focusBox(caret);
    if (!wasFull && trimmed.length === length) onComplete(trimmed);
  };

  const type = (i, raw) => {
    const only = raw.replace(/\D/g, '');
    if (!only) return;
    // Typing over a box mid-row, or an autofill dropping the whole code into
    // one: both are "digits arriving at position i".
    const next = (digits.join('').padEnd(length, ' ').slice(0, i)
                + only + digits.join('').slice(i + only.length))
      .replace(/ /g, '').slice(0, length);
    write(next, i + only.length);
  };

  const key = (i, e) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const cur = digits.join('');
      if (digits[i]) write(cur.slice(0, i) + cur.slice(i + 1), i);
      else if (i > 0) write(cur.slice(0, i - 1) + cur.slice(i), i - 1);
      return;
    }
    if (e.key === 'ArrowLeft') { e.preventDefault(); focusBox(i - 1); }
    if (e.key === 'ArrowRight') { e.preventDefault(); focusBox(i + 1); }
  };

  const paste = (i, e) => {
    const only = (e.clipboardData?.getData('text') || '').replace(/\D/g, '');
    if (!only) return;
    e.preventDefault();
    type(i, only);
  };

  return (
    <div
      role="group"
      aria-label={`${length}-digit code`}
      data-testid="code-boxes"
      style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}
    >
      {Array.from({ length }).map((_, i) => (
        <input
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          ref={(el) => { boxes.current[i] = el; }}
          id={i === 0 ? 'second-factor-code' : undefined}
          aria-label={`Digit ${i + 1} of ${length}`}
          type="text"
          inputMode="numeric"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocus && i === 0}
          disabled={disabled}
          // NOT maxLength={1}: a paste or an autofill has to be allowed to
          // arrive in one box before it can be spread across the row.
          value={digits[i] || ''}
          onChange={(e) => type(i, e.target.value)}
          onKeyDown={(e) => key(i, e)}
          onPaste={(e) => paste(i, e)}
          onFocus={(e) => e.target.select()}
          style={{
            width: '100%', minWidth: 0, boxSizing: 'border-box',
            padding: '14px 0', borderRadius: 11, textAlign: 'center',
            border: `1px solid ${invalid ? O.critical : O.hairlineOn}`,
            background: disabled ? 'rgba(13,61,42,0.04)' : O.card,
            color: O.ink, fontFamily: SANS, fontSize: 20, fontWeight: 700,
            outline: 'none',
          }}
        />
      ))}
    </div>
  );
};
